import { useContext } from 'react'

import { AbilitiesObject } from './usePermissionCheck.types'
import { IamStateContext } from '@percent/partner-dashboard/context/iam/iamContext/IamContext'
import { AbilityKey } from '@percent/partner-dashboard/context/iam/abilityDefinitions'

const defaultAbilitiesObject: AbilitiesObject = {
  viewApiKeys: false,
  manageApiKeys: false,
  viewDashboard: false,
  viewDonations: false,
  viewFundraisers: false,
  manageFundraisers: false,
  viewOpportunities: false,
  manageOpportunities: false,
  manageOpportunitySurveys: false,
  manageOpportunityWaivers: false,
  viewSurveyTemplates: false,
  manageSurveyTemplates: false,
  viewWaiverTemplates: false,
  manageWaiverTemplates: false,
  viewGivingCampaigns: false,
  manageGivingCampaigns: false,
  viewGivingActions: false,
  manageGivingActions: false,
  viewGrants: false,
  viewGrantPrograms: false,
  manageGrantReviews: false,
  manageGrantEvents: false,
  readGrantDocuments: false,
  uploadGrantDocuments: false,
  assignGrantsTasks: false,
  inviteGrantCandidates: false,
  manageDonationWallet: false,
  viewUsers: false,
  manageUsers: false,
  manageAdmins: false,
  manageDevelopers: false,
  manageMembers: false,
  manageOwners: false,
  manageGrantReviewers: false,
  viewWorkplaceGiving: false,
  viewCategories: false,
  manageCategories: false,
  viewNonprofits: false,
  viewOrganisations: false,
  viewVerifications: false,
  viewPartnerInsights: false
}

export const usePermissionCheck = () => {
  const context = useContext(IamStateContext)

  if (context === undefined) {
    throw new Error('useAllow must be used within a IamContext')
  }

  const checkAbilities = (permissions: AbilityKey[] | AbilityKey): boolean => {
    const abilities = { ...defaultAbilitiesObject }
    Object.keys(defaultAbilitiesObject).forEach(permissionName => {
      context.abilityList[permissionName as unknown as AbilityKey].forEach(rule => {
        if (context.permissions.includes(rule)) {
          abilities[permissionName as unknown as AbilityKey] = true
        }
      })
    })

    if (typeof permissions === 'string') {
      return abilities[permissions]
    }

    return permissions.every(permission => abilities[permission])
  }

  // Permissions are fetched asynchronously. `arePermissionsResolved` reports
  // whether that fetch has settled — succeeded or failed — so consumers can
  // avoid rendering permission-gated UI before permissions are known, while
  // still falling back to the no-access state if the fetch errors.
  const arePermissionsResolved = context.hasResolvedPermissions

  return { userCan: checkAbilities, arePermissionsResolved }
}
